import { BrandFamily, Domains, FlowType } from "@/core/types/types";

interface ExperimentEntry {
  brandFamily: BrandFamily;
  domain: Domains;
  flowDefinitions: Record<string, number>;
}

interface ExperimentConfig {
  experiments: ExperimentEntry[];
  enabled: boolean;
  defaultDefinition: FlowType;
}

// todo consider moving the config out for an easier modification
const config: ExperimentConfig = {
  experiments: [
    {
      brandFamily: BrandFamily.FLIGHTRIGHT,
      domain: Domains.DE,
      flowDefinitions: {
        [FlowType.APR_VAR]: 50,
        [FlowType.APR]: 50,
      },
    },
  ],
  enabled: false,
  defaultDefinition: FlowType.APR,
};

/**
 * Resolves the experiment flow based on the provided configuration.
 *
 * @returns {string} The selected flow definition or the default definition if the experiment is disabled or misconfigured.
 */
const resolveExperimentFlow = (
  brandFamily: BrandFamily,
  domain: Domains
): FlowType => {
  // If the experiment is not enabled or an entry is not found, return the default definition.
  const experiment = findExperimentByBrandConfig(brandFamily, domain);
  if (!config.enabled || !experiment) {
    return config.defaultDefinition;
  }

  const { flowDefinitions } = experiment;
  const totalProbability = Object.values(flowDefinitions).reduce(
    (sum, prob) => sum + prob,
    0
  );

  // If the total probability is not 100, log a warning and return the default definition.
  if (totalProbability !== 100) {
    console.warn(
      "Couldn't set up a flow definition experiment. Check the configuration.."
    );
    return config.defaultDefinition;
  }

  // Generate a random value between 0 and the total probability.
  const randomValue = Math.random() * totalProbability;
  let cumulativeProbability = 0;

  // Iterate over the flow definitions to determine which flow to return based on the random value.
  for (const [flowDefinition, probability] of Object.entries(flowDefinitions)) {
    cumulativeProbability += probability;
    if (randomValue <= cumulativeProbability) {
      return flowDefinition as FlowType;
    }
  }

  // Return the default definition if no other flow was selected.
  return config.defaultDefinition;
};

const findExperimentByBrandConfig = (
  brandFamily: BrandFamily,
  domain: Domains
): ExperimentEntry | null => {
  const { experiments } = config;
  return (
    experiments.find(
      (experiment: ExperimentEntry) =>
        experiment.brandFamily == brandFamily && experiment.domain == domain
    ) ?? null
  );
};

export default resolveExperimentFlow;
